import ApplicationController from '../application_controller'
import consumer from 'channels/consumer'
import { Modal } from 'bootstrap'

const FULL_DASH_ARRAY = 283
const WARNING_THRESHOLD = 30
const ALERT_THRESHOLD = 15
const TIME_LIMIT = 60

const COLOR_CODES = {
  info: {
    color: 'green'
  },
  warning: {
    color: 'orange',
    threshold: WARNING_THRESHOLD
  },
  alert: {
    color: 'red',
    threshold: ALERT_THRESHOLD
  }
}

class AppearanceController extends ApplicationController {
  static targets = ['modal', 'pathRemaining', 'timerLabel']

  connect () {
    const controller = this

    this.subscription = consumer.subscriptions.create(
      { channel: 'Users::AppearanceChannel' },
      {
        // Called once when the subscription is created.
        initialized () {
          this.update = this.update.bind(this)
        },

        // Called when the subscription is ready for use on the server.
        connected () {
          this.install()
          this.update()
        },

        // Called when the WebSocket connection is closed.
        disconnected () {
          this.uninstall()
        },

        // Called when the subscription is rejected by the server.
        rejected () {
          this.uninstall()
        },

        update () {
          this.documentIsActive() ? this.appear() : this.away()
        },

        appear () {
          // Calls `AppearanceChannel#appear` on the server.
          this.perform('appear')
        },

        reappear () {
          this.perform('reappear')
        },

        away () {
          // Calls `AppearanceChannel#away` on the server.
          this.perform('away')
        },

        logout () {
          this.perform('logout')
          window.location = '/users/sign_out'
        },

        received (data) {
          switch (data.message) {
            case 'warn':
              controller.modal.show()
              if (controller.counter === undefined) { controller.startCounter() }
              break
            case 'logout':
              this.logout()
              break
            case 'stop_counter':
              controller.stopCounter()
              controller.modal.hide()
              break
            default:
              console.log(data)
          }
        },

        mouseOver () {
          if (this.mouseOverTimestamp === undefined) { this.mouseOverTimestamp = Date.now() }

          if (Date.now() - this.mouseOverTimestamp > 1000) {
            this.perform('appear')
            this.mouseOverTimestamp = Date.now()
          }
        },

        install () {
          window.addEventListener('focus', this.update)
          window.addEventListener('blur', this.update)
          document.addEventListener('turbo:load', this.update)
          document.addEventListener('visibilitychange', this.update)
          document.addEventListener('keydown', this.update)
          document.addEventListener('mouseover', () => { this.mouseOver() })
        },

        uninstall () {
          window.removeEventListener('focus', this.update)
          window.removeEventListener('blur', this.update)
          document.removeEventListener('turbo:load', this.update)
          document.removeEventListener('visibilitychange', this.update)
          document.removeEventListener('keydown', this.update)
          document.removeEventListener('mouseover', () => { this.mouseOver() })
        },

        documentIsActive () {
          const state = document.visibilityState === 'visible' && document.hasFocus()
          return state
        }
      }
    )
  }

  get modal () {
    if (this._modal === undefined) {
      this._modal = new Modal(this.modalTarget, { backdrop: 'static', keyboard: false })
    }

    return this._modal
  }

  continueSession () {
    this.subscription.reappear()
  }

  startCounter () {
    let timePassed = 0
    let timeLeft = TIME_LIMIT

    const calculateTimeFraction = () => {
      const rawTimeFraction = timeLeft / TIME_LIMIT
      return rawTimeFraction - (1 / TIME_LIMIT) * (1 - rawTimeFraction)
    }

    const setCircleDasharray = () => {
      const circleDasharray = `${(
        calculateTimeFraction() * FULL_DASH_ARRAY
      ).toFixed(0)} 283`
      this.pathRemainingTarget.setAttribute('stroke-dasharray', circleDasharray)
    }

    // TIMER

    this.counter = setInterval(() => {
      timePassed = timePassed += 1
      timeLeft = TIME_LIMIT - timePassed
      this.timerLabelTarget.innerHTML = formatTime(timeLeft)

      setCircleDasharray()
      this.setRemainingPathColor(timeLeft)

      if (timeLeft === 0) {
        clearInterval(this.counter)
        this.subscription.logout()
      }
    }, 1000)
  }

  stopCounter () {
    clearInterval(this.counter)
    this.pathRemainingTarget.classList.remove('orange', 'red')
    this.pathRemainingTarget.classList.add('green')
    if (this.hasCountdownTarget) { this.timerLabelTarget.innerHTML = '' }
    delete this.counter
  }

  disconnect () {
    this.subscription.unsubscribe()
  }

  setRemainingPathColor (timeLeft) {
    const { alert, warning, info } = COLOR_CODES
    if (timeLeft <= alert.threshold) {
      this.pathRemainingTarget
        .classList.remove(warning.color)
      this.pathRemainingTarget
        .classList.add(alert.color)
    } else if (timeLeft <= warning.threshold) {
      this.pathRemainingTarget
        .classList.remove(info.color)
      this.pathRemainingTarget
        .classList.add(warning.color)
    }
  }
}

// ========================================== //
// UTILS                                      //
// ========================================== //

function formatTime (time) {
  const minutes = Math.floor(time / 60)
  let seconds = time % 60

  if (seconds < 10) {
    seconds = `0${seconds}`
  }

  return `${minutes}:${seconds}`
}

export default AppearanceController
